import { openingPrompts, templates } from './data/templates'
import type {
  CategoryProgress,
  ChecklistTemplate,
  ClosingReview,
  Observation,
  Opportunity,
  TemplateKind,
  VisitDetails,
  VisitSnapshot,
} from './types'

export const nowIso = () => new Date().toISOString()
export const makeId = () => crypto.randomUUID()
export const getVisitTemplate = (visit: VisitSnapshot) => visit.templateSnapshot ?? templates[visit.templateId] ?? templates.blank

const emptyDetails: VisitDetails = {
  client: '',
  siteName: '',
  address: '',
  siteContact: '',
  emergencyContact: '',
  visitDate: new Date().toISOString().slice(0, 10),
  auditors: '',
  siteType: '',
  principalActivities: '',
  operatingPattern: '',
  weather: '',
  actualActivity: '',
  visitConditions: '',
  accessAndPpe: '',
  restrictions: '',
  knownIssues: '',
  plannedChanges: '',
  peopleToInterview: '',
  recordsAvailable: '',
}

const emptyClosing: ClosingReview = {
  findingsDiscussed: '',
  immediateActions: '',
  feasibilityProjects: '',
  informationToSupply: '',
  limitations: '',
  followUpContact: '',
  followUpDate: '',
  mainSystems: '',
  mainOpportunities: '',
  operationalIssues: '',
  dataGaps: '',
  nextSteps: '',
  auditorSignOff: '',
  clientAcknowledgement: '',
}

export function createVisit(templateId: TemplateKind = 'buildings', slug = 'demo'): VisitSnapshot {
  const template = templates[templateId]
  if (!template) throw new Error(`Unknown checklist template: ${templateId}`)
  return createVisitFromTemplate(template, slug)
}

export function createVisitFromTemplate(template: ChecklistTemplate, slug: string): VisitSnapshot {
  const categoryProgress = Object.fromEntries(
    template.categories.map((category) => [
      category.id,
      { state: 'not_reviewed', note: '' } satisfies CategoryProgress,
    ]),
  )
  return {
    id: makeId(),
    slug,
    templateId: template.id,
    templateVersion: template.version,
    templateSnapshot: JSON.parse(JSON.stringify(template)),
    details: {
      ...emptyDetails,
      client: slug === 'demo' ? 'Example Client' : '',
      siteName: slug === 'demo' ? 'Demonstration Site' : '',
      siteType: template.label,
    },
    areas: [],
    categoryProgress,
    quickChecks: Object.fromEntries(template.quickChecks.map((item) => [item.id, { answer: '', evidence: '' }])),
    openingResponses: Object.fromEntries(openingPrompts.map((item, index) => [`opening-${index + 1}`, ''])),
    observations: [],
    opportunities: [],
    evidence: [],
    closing: { ...emptyClosing },
    contributorName: '',
    recordRevisions: {},
    updatedAt: nowIso(),
    revision: 0,
  }
}

export function normaliseVisit(visit: VisitSnapshot): VisitSnapshot {
  const template = visit.templateSnapshot && Array.isArray(visit.templateSnapshot.categories)
    ? visit.templateSnapshot
    : templates[visit.templateId] ?? templates.blank
  return {
    ...visit,
    templateSnapshot: template,
    details: { ...emptyDetails, ...visit.details },
    openingResponses: visit.openingResponses ?? Object.fromEntries(
      openingPrompts.map((_item, index) => [`opening-${index + 1}`, '']),
    ),
    recordRevisions: visit.recordRevisions ?? {},
    opportunities: (visit.opportunities ?? []).map((item) => ({
      ...item,
      developmentStatus: item.developmentStatus ?? 'candidate',
      areaIds: item.areaIds ?? [],
      benefitTypes: item.benefitTypes ?? [],
    })),
    evidence: visit.evidence ?? [],
  }
}

export function changeTemplate(visit: VisitSnapshot, templateId: TemplateKind): VisitSnapshot {
  const template = templates[templateId]
  if (!template) return visit
  const progress = Object.fromEntries(
    template.categories.map((category) => [
      category.id,
      visit.categoryProgress[category.id] ?? { state: 'not_reviewed', note: '' },
    ]),
  )
  return {
    ...visit,
    templateId,
    templateVersion: template.version,
    templateSnapshot: JSON.parse(JSON.stringify(template)),
    categoryProgress: progress,
    quickChecks: Object.fromEntries(
      template.quickChecks.map((item) => [
        item.id,
        visit.quickChecks[item.id] ?? { answer: '', evidence: '' },
      ]),
    ),
    updatedAt: nowIso(),
  }
}

export function removeAreaFromVisit(visit: VisitSnapshot, areaId: string): VisitSnapshot {
  const timestamp = nowIso()
  return {
    ...visit,
    areas: visit.areas.filter((area) => area.id !== areaId),
    observations: visit.observations.map((observation) => (
      observation.areaId === areaId
        ? { ...observation, areaId: '', updatedAt: timestamp }
        : observation
    )),
    opportunities: visit.opportunities.map((opportunity) => (
      opportunity.areaIds.includes(areaId)
        ? {
            ...opportunity,
            areaIds: opportunity.areaIds.filter((id) => id !== areaId),
            updatedAt: timestamp,
          }
        : opportunity
    )),
    updatedAt: timestamp,
  }
}

export function createObservation(categoryId: string, areaId: string, author: string): Observation {
  const timestamp = nowIso()
  return {
    id: makeId(),
    categoryId,
    areaId,
    assetLabel: '',
    condition: 'not_known',
    note: '',
    dataPoints: [],
    evidenceSource: [],
    implementationConstraint: '',
    followUp: '',
    followUpOwner: '',
    followUpDate: '',
    evidenceIds: [],
    createdAt: timestamp,
    updatedAt: timestamp,
    createdBy: author,
    revision: 0,
  }
}

export function createOpportunity(
  categoryId: string,
  observationIds: string[],
  author: string,
): Opportunity {
  const timestamp = nowIso()
  return {
    id: makeId(),
    developmentStatus: 'candidate',
    title: '',
    areaIds: [],
    categoryId,
    observationIds,
    observed: '',
    recommendation: '',
    benefitTypes: [],
    priority: 'medium',
    confidence: 'medium',
    difficulty: 'unknown',
    constraints: '',
    responsibleContact: '',
    targetFollowUp: '',
    dataNeeded: '',
    disposition: 'candidate',
    notPursuedReason: '',
    createdAt: timestamp,
    updatedAt: timestamp,
    createdBy: author,
    revision: 0,
  }
}

export interface ReviewWarning {
  id: string
  level: 'important' | 'check'
  title: string
  detail: string
  target: 'visit' | 'walk' | 'opportunities' | 'review'
}

export function getReviewWarnings(visit: VisitSnapshot): ReviewWarning[] {
  const warnings: ReviewWarning[] = []
  const template = getVisitTemplate(visit)

  template.categories.forEach((category) => {
    const progress = visit.categoryProgress[category.id]
    if (!progress || progress.state === 'not_reviewed') {
      warnings.push({
        id: `category-${category.id}`,
        level: 'check',
        title: `${category.label} has not been reviewed`,
        detail: 'Open it during the walk, or mark it not applicable with a reason.',
        target: 'walk',
      })
    }
    if (progress?.state === 'not_applicable' && !progress.note.trim()) {
      warnings.push({
        id: `na-${category.id}`,
        level: 'important',
        title: `${category.label} needs an N/A reason`,
        detail: 'Add a short explanation so the omission is clear later.',
        target: 'walk',
      })
    }
  })

  visit.observations.forEach((observation) => {
    if (!observation.areaId) {
      warnings.push({
        id: `area-${observation.id}`,
        level: 'check',
        title: 'Observation has no area',
        detail: observation.note || observation.assetLabel || 'Unnamed observation',
        target: 'walk',
      })
    }
    if (!observation.note.trim() && !observation.assetLabel.trim()) {
      warnings.push({
        id: `note-${observation.id}`,
        level: 'important',
        title: 'Observation needs a note or asset label',
        detail: 'Add enough context to understand what was seen.',
        target: 'walk',
      })
    }
    if (observation.followUp.trim() && !observation.followUpOwner.trim()) {
      warnings.push({
        id: `follow-up-owner-${observation.id}`,
        level: 'important',
        title: 'Follow-up has no owner',
        detail: observation.followUp,
        target: 'walk',
      })
    }
  })

  const unsyncedEvidence = visit.evidence.filter((item) => !item.deletedAt && item.localOnly)
  if (unsyncedEvidence.length) {
    warnings.push({
      id: 'unsynced-evidence',
      level: 'important',
      title: `${unsyncedEvidence.length} evidence item${unsyncedEvidence.length === 1 ? '' : 's'} only on this device`,
      detail: 'Reconnect and wait for “Synced at…” before leaving site.',
      target: 'review',
    })
  }

  visit.opportunities.forEach((opportunity) => {
    if (!opportunity.title.trim() || !opportunity.recommendation.trim()) {
      warnings.push({
        id: `opportunity-${opportunity.id}`,
        level: 'important',
        title: 'Opportunity candidate is incomplete',
        detail: opportunity.title || 'Untitled opportunity',
        target: 'opportunities',
      })
    }
    if (!opportunity.observationIds.length) {
      warnings.push({
        id: `opportunity-evidence-${opportunity.id}`,
        level: 'check',
        title: 'Opportunity candidate has no linked observation',
        detail: opportunity.title || 'Untitled opportunity',
        target: 'opportunities',
      })
    }
  })

  if (visit.areas.some((area) => !area.accessible && !area.excludedReason?.trim())) {
    warnings.push({
      id: 'area-exclusion-reasons',
      level: 'important',
      title: 'An inaccessible area needs a reason',
      detail: 'Record why access was not possible.',
      target: 'visit',
    })
  }

  if (!Object.values(visit.closing).some((value) => value.trim())) {
    warnings.push({
      id: 'close-out',
      level: 'check',
      title: 'Close-out has not been recorded',
      detail: 'Capture the discussion, agreed actions and outstanding information before leaving.',
      target: 'review',
    })
  }

  return warnings
}

export function getVisitSummary(visit: VisitSnapshot) {
  const categoryValues = Object.values(visit.categoryProgress)
  return {
    areas: visit.areas.length,
    reviewed: categoryValues.filter((item) => item.state === 'reviewed' || item.state === 'not_applicable').length,
    categories: categoryValues.length,
    observations: visit.observations.length,
    opportunities: visit.opportunities.length,
    followUps: visit.observations.filter((item) => item.followUp.trim()).length,
    unsyncedMedia: visit.evidence.filter((item) => item.localOnly).length,
  }
}
